import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Target, Eye, MapPin, Phone, Mail, Clock, Users, Award, BookOpen, GraduationCap, UserCheck, X, Quote, Check } from 'lucide-react';

import fullLogo from '../assets/full_logo.jpg';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = API_URL;

const AboutPage = () => {
  const [centers, setCenters] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [directors, setDirectors] = useState([]);
  const [selectedDirector, setSelectedDirector] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [centersRes, faqsRes, directorsRes] = await Promise.all([
          axios.get(`${API_URL}/api/centers`),
          axios.get(`${API_URL}/api/faqs`),
          axios.get(`${API_URL}/api/directors`)
        ]);
        setCenters(centersRes.data);
        setFaqs(faqsRes.data);
        setDirectors(directorsRes.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    fetchData();
  }, []);

  return (
    <Layout>
      {/* Hero */}
      <section className="bg-gradient-to-br from-slate-50 via-rose-50 to-slate-50 py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-3xl mx-auto"
          >
            <Badge className="mb-4">About Us</Badge>
            <h1 className="font-heading text-4xl md:text-5xl font-bold mb-6">
              Shaping Futures, <span className="text-primary">One Student at a Time</span>
            </h1>
            <p className="text-lg text-muted-foreground">
              For over 5 years, Caliber Nova has been the premier destination for JEE and NEET aspirants,
              helping thousands of students achieve their dreams of joining prestigious institutions.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Institute Overview */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="font-heading text-3xl md:text-4xl font-bold mb-6">
                Our <span className="text-primary">Story</span>
              </h2>
              <div className="space-y-4 text-muted-foreground">
                <p>
                  At Caliber Nova, we believe every student's success begins with a strong foundation. Established in 2020, our institute was built with a simple vision to provide quality education, personalized guidance, and the confidence students need to achieve their academic goals.
                </p>
                <p>
                  From Foundation Programs for Classes 7th to 10th to coaching for 11th & 12th, JEE, NEET, MHT-CET, and professional courses, we focus on concept-based learning, individual attention, and continuous growth.
                </p>
                <p>
                  Our experienced faculty and student-centric approach create an environment where learning becomes engaging, effective, and result-oriented. Through regular assessments, doubt-solving sessions, and dedicated mentorship, we help students strengthen their understanding and perform with confidence.
                </p>
                <p className="font-medium text-slate-800">
                  At Caliber Nova, we don't just prepare students for exams. We help them build the knowledge, skills, and confidence needed for a successful future.
                </p>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-8">
                {[
                  { value: '5000+', label: 'Students Mentored' },
                  { value: '90%+', label: 'Academic Excellence' },
                  { value: '20+', label: 'Programs & Courses' },
                  { value: '10+', label: 'Years of Commitment' },
                ].map((stat, index) => (
                  <div key={index} className="text-center">
                    <p className="font-heading text-3xl font-bold text-primary">{stat.value}</p>
                    <p className="text-sm text-muted-foreground">{stat.label}</p>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="rounded-2xl overflow-hidden shadow-2xl bg-white">
                <img
                  src={fullLogo}
                  alt="Caliber Nova Institute"
                  className="w-full h-[400px] object-contain p-4"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-white rounded-xl shadow-xl p-4 hidden md:block">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                    <GraduationCap className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <p className="font-heading font-bold text-lg">Since 2020</p>
                    <p className="text-sm text-muted-foreground">5+ Years of Excellence</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Empowering Students Section */}
      <section className="py-16 md:py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <Badge className="mb-4">Our Commitment</Badge>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Empowering Students Through <span className="text-primary">Excellence in Education</span>
            </h2>
            <p className="text-muted-foreground">
              At Caliber Nova, we don't just prepare students for exams—we help them develop the knowledge, confidence, and skills needed for lifelong success. Through expert teaching, personalized mentorship, and a structured learning environment, we guide students toward achieving their full potential.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {[
              {
                title: "Experienced Faculty",
                desc: "Learn from dedicated educators who focus on concept clarity, academic excellence, and continuous student growth."
              },
              {
                title: "Personalized Attention",
                desc: "Small batch sizes and individual mentoring ensure every student receives the support they need to succeed."
              },
              {
                title: "Regular Tests & Performance Tracking",
                desc: "Systematic assessments help students identify strengths, improve weaknesses, and stay exam-ready."
              },
              {
                title: "Comprehensive Study Material",
                desc: "Well-structured notes, practice questions, and learning resources designed for better understanding and retention."
              }
            ].map((item, idx) => (
              <div key={idx} className="flex gap-4 p-6 bg-white rounded-2xl shadow-sm border border-slate-100">
                <div className="w-8 h-8 rounded-full bg-green-50 flex items-center justify-center shrink-0 text-green-600">
                  <Check className="w-5 h-5" />
                </div>
                <div>
                  <h4 className="font-heading font-semibold text-slate-900 mb-1 text-base">{item.title}</h4>
                  <p className="text-slate-600 text-sm leading-relaxed">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <p className="font-heading font-medium text-slate-900 mb-4">
              🎯 Join Caliber Nova — Where Strong Foundations Create Bright Futures.
            </p>
            <Button asChild className="bg-primary hover:bg-primary/90">
              <Link to="/courses">Learn More</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Mission & Values Section */}
      <section className="py-20 bg-primary text-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
                  <Target className="w-6 h-6" />
                </div>
                <h3 className="font-heading text-3xl font-bold">Our Mission</h3>
              </div>
              <div className="space-y-4 text-white/90 text-lg leading-relaxed">
                <p>
                  At Caliber Nova, our mission is to empower students with the knowledge, skills, and confidence they need to achieve academic excellence and build successful careers. We are committed to creating a learning environment that encourages curiosity, critical thinking, and continuous growth.
                </p>
                <p>
                  Through quality education, personalized mentorship, and a student-first approach, we help learners unlock their full potential. We believe that strong foundations lead to greater achievements. That's why our focus goes beyond examinations—we aim to develop disciplined, confident, and future-ready individuals.
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              <h3 className="font-heading text-2xl font-bold mb-6 flex items-center gap-2">
                Our Core Values
              </h3>
              <div className="grid sm:grid-cols-2 gap-6">
                {[
                  { title: "Excellence", desc: "Striving for the highest academic and coaching standards in everything we do." },
                  { title: "Integrity", desc: "Building trust through transparent policies and ethical, honest communication." },
                  { title: "Dedication", desc: "Committed to supporting every student's learning journey and personal growth." },
                  { title: "Student-Centric", desc: "Placing students' academic goals, doubts, and needs at the heart of our institute." }
                ].map((val, idx) => (
                  <div key={idx} className="p-5 rounded-xl bg-white/10 border border-white/15 backdrop-blur-sm">
                    <h4 className="font-heading font-semibold text-white mb-2 text-base">{val.title}</h4>
                    <p className="text-white/80 text-sm leading-relaxed">{val.desc}</p>
                  </div>
                ))}
              </div>
              <p className="mt-8 italic text-white/90 text-base font-medium">
                "At Caliber Nova, we strive to inspire every student to dream bigger, work harder, and achieve more."
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Leadership Section */}
      {directors.length > 0 && (
        <section className="py-16 md:py-24 bg-slate-50">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <Badge className="mb-4">Leadership</Badge>
              <h2 className="font-heading text-3xl md:text-4xl font-bold">
                The Minds Behind <span className="text-primary">Caliber Nova</span>
              </h2>
              <p className="text-muted-foreground mt-4 max-w-2xl mx-auto">
                Meet the visionaries who built Caliber Nova from the ground up — IITians and doctors dedicated to transforming education in Nagpur.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 gap-8">
              {directors.map((director, index) => (
                <motion.div
                  key={director.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.15 }}
                >
                  <Card className="h-full overflow-hidden group card-hover border-0 shadow-lg">
                    <CardContent className="p-0">
                      <div className="flex flex-col sm:flex-row">
                        {/* Photo */}
                        <div className="sm:w-44 flex-shrink-0">
                          {director.photo_url ? (
                            <img
                              src={director.photo_url?.startsWith('http') ? director.photo_url : `${API_BASE}${director.photo_url}`}
                              alt={director.name}
                              className="w-full h-52 sm:h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-52 sm:h-full bg-primary/10 flex items-center justify-center">
                              <UserCheck className="w-16 h-16 text-primary/40" />
                            </div>
                          )}
                        </div>
                        {/* Content */}
                        <div className="flex flex-col justify-between p-6 flex-1">
                          <div>
                            <h3 className="font-heading text-xl font-bold mb-1">{director.name}</h3>
                            <p className="text-primary font-medium text-sm mb-3">{director.designation}</p>
                            <p className="text-muted-foreground text-sm leading-relaxed line-clamp-4">{director.bio}</p>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            className="mt-4 self-start group-hover:bg-primary group-hover:text-white transition-colors"
                            onClick={() => setSelectedDirector(director)}
                          >
                            Read More
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Director Detail Popup */}
      <AnimatePresence>
        {selectedDirector && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
            onClick={() => setSelectedDirector(null)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.85, y: 40 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.85, y: 40 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header with photo */}
              <div className="relative bg-gradient-to-br from-primary to-primary/70 p-8 text-white rounded-t-2xl">
                <button
                  onClick={() => setSelectedDirector(null)}
                  className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
                <div className="flex items-center gap-6">
                  <motion.div
                    initial={{ scale: 0.5, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ delay: 0.1 }}
                    className="w-24 h-24 rounded-full overflow-hidden border-4 border-white/40 flex-shrink-0"
                  >
                    {selectedDirector.photo_url ? (
                      <img
                        src={selectedDirector.photo_url?.startsWith('http') ? selectedDirector.photo_url : `${API_BASE}${selectedDirector.photo_url}`}
                        alt={selectedDirector.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full bg-white/20 flex items-center justify-center">
                        <UserCheck className="w-10 h-10 text-white" />
                      </div>
                    )}
                  </motion.div>
                  <motion.div
                    initial={{ x: -20, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ delay: 0.15 }}
                  >
                    <h2 className="font-heading text-2xl font-bold">{selectedDirector.name}</h2>
                    <p className="text-white/80 font-medium mt-1">{selectedDirector.designation}</p>
                  </motion.div>
                </div>
              </div>

              {/* Body */}
              <div className="p-8 space-y-6">
                <motion.div
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.2 }}
                >
                  <h4 className="font-heading font-semibold text-lg mb-2">About</h4>
                  <p className="text-muted-foreground leading-relaxed">{selectedDirector.bio}</p>
                </motion.div>

                <motion.div
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.25 }}
                  className="bg-primary/5 rounded-xl p-6 border-l-4 border-primary"
                >
                  <div className="flex items-center gap-2 mb-3">
                    <Quote className="w-5 h-5 text-primary" />
                    <h4 className="font-heading font-semibold text-lg text-primary">From the Director's Desk</h4>
                  </div>
                  <p className="text-muted-foreground leading-relaxed italic">{selectedDirector.desk_message}</p>
                </motion.div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>



      {/* Centers */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <Badge className="mb-4">Our Centers</Badge>
            <h2 className="font-heading text-3xl md:text-4xl font-bold">
              Caliber Nova <span className="text-primary">Locations</span>
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {centers.length > 0 ? centers.map((center, index) => (
              <motion.div
                key={center.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="h-full" data-testid={`center-${center.id}`}>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <MapPin className="w-5 h-5 text-primary" />
                      {center.name}
                      {center.is_main && <Badge variant="secondary">Main</Badge>}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3 text-sm">
                    <p className="text-muted-foreground">{center.address}, {center.city}, {center.state} - {center.pincode}</p>
                    <div className="flex items-center gap-2">
                      <Phone className="w-4 h-4 text-muted-foreground" />
                      <span>{center.phone}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Mail className="w-4 h-4 text-muted-foreground" />
                      <span>{center.email}</span>
                    </div>
                    {center.map_url && (
                      <a href={center.map_url} target="_blank" rel="noopener noreferrer" className="text-primary text-sm hover:underline">
                        View on Map →
                      </a>
                    )}
                  </CardContent>
                </Card>
              </motion.div>
            )) : (
              // Placeholder
              <Card className="lg:col-span-3">
                <CardContent className="py-12 text-center">
                  <MapPin className="w-12 h-12 mx-auto text-muted-foreground/30 mb-4" />
                  <h3 className="font-heading font-semibold mb-2">Kota Main Campus</h3>
                  <p className="text-muted-foreground">Kota, Rajasthan, India</p>
                  <p className="text-sm text-muted-foreground mt-2">Contact: +91 9876543210</p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </section>

      {/* FAQs / Help Center */}
      <section className="py-16 md:py-24">
        <div className="max-w-3xl mx-auto px-4 md:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <Badge className="mb-4">Help Center</Badge>
            <h2 className="font-heading text-3xl md:text-4xl font-bold">
              Frequently Asked <span className="text-primary">Questions</span>
            </h2>
          </motion.div>

          <Accordion type="single" collapsible className="w-full">
            {faqs.length > 0 ? faqs.map((faq, index) => (
              <AccordionItem key={faq.id} value={`faq-${index}`} data-testid={`faq-${index}`}>
                <AccordionTrigger className="text-left font-medium">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            )) : (
              // Default FAQs
              [
                { q: 'What courses does Caliber Nova offer?', a: 'We offer comprehensive coaching for JEE Mains, JEE Advanced, NEET UG, NEET PG, and Foundation courses for classes 9-10.' },
                { q: 'How can I apply for admission?', a: 'You can apply online through our website or visit our center for offline registration. Our team will guide you through the process.' },
                { q: 'Are scholarships available?', a: 'Yes, we offer merit-based scholarships up to 100% for deserving students. Apply through our scholarship program.' },
                { q: 'What is the batch timing?', a: 'We offer flexible batch timings including morning, afternoon, and evening batches to suit your schedule.' },
              ].map((faq, index) => (
                <AccordionItem key={index} value={`faq-${index}`}>
                  <AccordionTrigger className="text-left font-medium">
                    {faq.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground">
                    {faq.a}
                  </AccordionContent>
                </AccordionItem>
              ))
            )}
          </Accordion>
        </div>
      </section>
    </Layout>
  );
};

export default AboutPage;
